import { FormArray, FormControl, FormGroup } from "@angular/forms";
import { BmItemForm, BmTotal, BudgetModelFormGroup } from "../../../pojo/bm-form";
import { BmItem } from "../../../pojo/bm-classes";
import { BmDefaultFormCreatorService } from "../../general/bm-default-form-creator.service";
import { BmCalcHelperService } from "../../general/bm-calc-helper.service";
import { BmCalcType } from "../../../pojo/bm-enums";

export class BmTotalGeneralCalcService {
  public static createForm(yearsNumber: number): FormGroup<BmTotal> {
    const items: BmItem[] = BmDefaultFormCreatorService.createItemsArray(yearsNumber);

    return new FormGroup<BmTotal>({
      values: new FormArray<FormGroup<BmItemForm>>(
        items.map((data) => BmCalcHelperService.createBmItemForm(data)),
      ),
      totalRow: new FormControl<number>(0),
      pcnt: new FormArray<FormGroup<BmItemForm>>(
        items.map((data) => BmCalcHelperService.createBmItemForm(data)),
      ),
      totalRowPcnt: new FormControl<number>(0),
    });
  }

  /**
   * Calculate total values with/without investment data section, based on {@link includeInvestment }.
   * Calculate sum of each section for each period
   *
   * @param budgetForm model data
   * @param totalFormGroup selected form group (Total expenses or Capital required)
   * @param includeInvestment flag to show if investment data is included in calculation
   */
  static recalculateTotalExpenses(budgetForm: BudgetModelFormGroup, totalFormGroup: FormGroup<BmTotal>, includeInvestment: boolean): void {
    // do not change values in total commitment edit mode
    if (budgetForm.controls.totalCommitment.enabled) {
      return;
    }

    const md = budgetForm.controls.modelData;
    const mgmTotals = md.controls.mgmFee.controls.total;
    const operationalTotals = md.controls.operatingExpenses.controls.total;
    const organizationalTot = md.controls.organizationalExpenses.controls.total;
    const investmentTotals = md.controls.investment.controls.itemsValue;

    const totalCommitment = budgetForm.controls.totalCommitment.getRawValue() || 0;
    const valuesFA = totalFormGroup.controls.values;
    const pcntFA = totalFormGroup.controls.pcnt;


    for (let i = 0; i < valuesFA.length; i++) {
      const sum =
        (Number(mgmTotals.at(i).controls.itemValue.getRawValue()) || 0) +
        (Number(operationalTotals.at(i).controls.itemValue.getRawValue()) || 0) +
        (Number(organizationalTot.at(i).controls.itemValue.getRawValue()) || 0) +
        (includeInvestment ? (Number(investmentTotals.at(i).controls.itemValue.getRawValue()) || 0) : 0);
      const pct = totalCommitment > 0 ? (sum / totalCommitment) * 100 : 0;

      BmCalcHelperService.patchAndDisable(valuesFA.at(i), sum, BmCalcType.CALCULATED);
      BmCalcHelperService.patchAndDisable(pcntFA.at(i), pct, BmCalcType.CALCULATED);
    }


    totalFormGroup.controls.totalRow.patchValue(BmCalcHelperService.recalculateRowTotal(valuesFA), { emitEvent: false });
    totalFormGroup.controls.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(totalFormGroup.getRawValue().totalRow, budgetForm.getRawValue().modelData.totalCapRequired.totalRow), { emitEvent: false });
  }

  /**
   * Calculate totalRowPcnt for all groups
   *
   * @param budgetForm all model data
   */
  public static recalculateAllRowValuePcnt(budgetForm: BudgetModelFormGroup): void {
    const modelData = budgetForm.controls.modelData.controls;
    const totalCapRequired = modelData.totalCapRequired.controls.totalRow.getRawValue();

    // Mgm Fee section
    const mgmFeeSection = modelData.mgmFee.controls;
    mgmFeeSection.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(mgmFeeSection.totalRow.getRawValue(), totalCapRequired));
    // Mgm Fee group
    modelData.mgmFee.controls.groups.controls.forEach((mgmGroup) => {
      mgmGroup.controls.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(mgmGroup.controls.totalRow.getRawValue(), totalCapRequired));
      const vatValues = mgmGroup.controls.subGroups.controls.vatValues.controls;
      vatValues.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(vatValues.totalRow.getRawValue(), totalCapRequired));
      const mgmFeeValues = mgmGroup.controls.subGroups.controls.mgmFeeValues.controls;
      mgmFeeValues.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(mgmFeeValues.totalRow.getRawValue(), totalCapRequired));
      const catchUpValues = mgmGroup.controls.subGroups.controls.catchUp.controls;
      catchUpValues.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(catchUpValues.totalRow.getRawValue(), totalCapRequired));
    });

    // Operating expenses section
    const opExpSection = modelData.operatingExpenses.controls;
    opExpSection.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(opExpSection.totalRow.getRawValue(), totalCapRequired));
    opExpSection.groups.controls.forEach((opGroup) => {
      opGroup.controls.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(opGroup.controls.totalRow.getRawValue(), totalCapRequired));
    });

    // Organizational expenses section
    const orgExpSection = modelData.organizationalExpenses.controls;
    orgExpSection.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(orgExpSection.totalRow.getRawValue(), totalCapRequired));
    orgExpSection.groups.controls.forEach((orgGroup) => {
      orgGroup.controls.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(orgGroup.controls.totalRow.getRawValue(), totalCapRequired));
    });
    // Investment section
    const investmentSection = modelData.investment.controls;
    investmentSection.totalRowPcnt.patchValue(BmCalcHelperService.recalculateRowPcntTotal(investmentSection.totalRow.getRawValue(), totalCapRequired));
  }
}
